
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import BuyerLayout from "@/components/layouts/BuyerLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardFooter, CardDescription } from "@/components/ui/card";
import { useToast } from "@/components/ui/use-toast";
import { Search as SearchIcon, Building, MapPin, ArrowRight, Loader2 } from "lucide-react";
import { Association } from "@/models";
import { associationApi } from "@/services/api";
import { formatAssociationAddress } from "@/utils/modelUtils";
import { extractDataArray } from "@/utils/apiUtils";

const Search = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const [isSearching, setIsSearching] = useState(false);
  const [searchResults, setSearchResults] = useState<Association[]>([]);
  const navigate = useNavigate();
  const { toast } = useToast();

  const { data: associationsResponse = [], isLoading } = useQuery({
    queryKey: ['associations', {}],
    queryFn: associationApi.list,
    meta: {
      onError: (error: any) => {
        toast({
          variant: "destructive",
          title: "Error loading associations",
          description: error.message || "Failed to fetch associations",
        });
      }
    }
  });

  // Extract associations array from potential paginated response
  const associations = extractDataArray<Association>(associationsResponse);

  const handleSearch = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    
    if (!searchTerm.trim()) {
      toast({
        variant: "destructive",
        title: "Search error",
        description: "Please enter a search term.",
      });
      return;
    }
    
    setIsSearching(true);
    
    if (associations) {
      const results = associations.filter(
        (assoc) =>
          assoc.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
          (assoc.default_property_address && assoc.default_property_address.toLowerCase().includes(searchTerm.toLowerCase())) ||
          (assoc.default_property_city && assoc.default_property_city.toLowerCase().includes(searchTerm.toLowerCase())) ||
          (assoc.default_property_state && assoc.default_property_state.toLowerCase().includes(searchTerm.toLowerCase())) ||
          (assoc.default_property_zip && assoc.default_property_zip.toLowerCase().includes(searchTerm.toLowerCase()))
      );
      
      setSearchResults(results);
    } else {
      setSearchResults([]);
    }
    
    setIsSearching(false);
  };

  const handleSelectAssociation = (associationId: number) => {
    navigate(`/buyer/request/${associationId}`);
  };

  return (
    <BuyerLayout>
      <div className="space-y-6">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Find Documents</h1>
          <p className="text-gray-500">
            Search for an association or property to request documents.
          </p>
        </div>

        <Card>
          <CardHeader>
            <CardTitle>Search</CardTitle>
            <CardDescription>
              Enter an association name, property address, or city
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSearch} className="space-y-4">
              <div className="grid grid-cols-1 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="search">Search Term</Label>
                  <div className="flex w-full max-w-lg items-center space-x-2">
                    <Input
                      id="search"
                      type="text"
                      placeholder="Association name or address"
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                    />
                    <Button type="submit" disabled={isSearching || isLoading}>
                      {isSearching ? (
                        <Loader2 className="h-4 w-4 animate-spin mr-2" />
                      ) : (
                        <SearchIcon className="mr-2 h-4 w-4" />
                      )}
                      Search
                    </Button>
                  </div>
                </div>
              </div>
            </form>
          </CardContent>
        </Card>

        {isLoading && (
          <div className="flex justify-center py-8">
            <Loader2 className="h-8 w-8 animate-spin text-brand-blue" />
          </div>
        )}

        {searchResults.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle>Results</CardTitle>
              <CardDescription>
                Found {searchResults.length} association
                {searchResults.length !== 1 ? "s" : ""}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {searchResults.map((association) => (
                  <div
                    key={association.id}
                    className="flex flex-col sm:flex-row items-start sm:items-center justify-between border rounded-lg p-4 gap-3"
                  >
                    <div className="flex items-start space-x-4">
                      <div className="flex items-center justify-center h-10 w-10 rounded-full bg-brand-blue/10 text-brand-blue">
                        <Building className="h-5 w-5" />
                      </div>
                      <div>
                        <div className="font-medium">{association.name}</div>
                        <div className="text-sm text-muted-foreground flex items-center mt-1">
                          <MapPin className="h-3 w-3 mr-1" />
                          {formatAssociationAddress(association)}
                        </div>
                        <div className="text-xs text-muted-foreground mt-1">
                          Type: {association.association_type}
                        </div>
                      </div>
                    </div>
                    <Button onClick={() => handleSelectAssociation(association.id)}>
                      Select
                      <ArrowRight className="ml-2 h-4 w-4" />
                    </Button>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {searchTerm && searchResults.length === 0 && !isSearching && !isLoading && (
          <Card>
            <CardContent className="py-6">
              <div className="text-center">
                <p className="text-lg font-medium">No associations found</p>
                <p className="text-muted-foreground mt-1">
                  We couldn't find any associations matching "{searchTerm}".
                </p>
              </div>
            </CardContent>
            <CardFooter className="flex justify-center">
              <Button variant="outline" onClick={() => setSearchTerm("")}>
                Try a different search
              </Button>
            </CardFooter>
          </Card>
        )}
      </div>
    </BuyerLayout>
  );
};

export default Search;
